import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Refrigerator, Package2, Snowflake, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";

interface InventoryItem {
  id: string;
  item: string;
  quantity?: number;
  unit?: string | null;
  type: string;
  expiry_date: string | null;
}

const fetchInventoryItems = async () => {
  const { data, error } = await supabase
    .from('stock_lists')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

const deleteInventoryItem = async (id: string) => {
  const { error } = await supabase
    .from('stock_lists')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

interface ListItemProps {
  item: InventoryItem;
  onDelete: (id: string) => Promise<void>;
}

const ListItem = ({ item, onDelete }: ListItemProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(item.id);
    } catch (error) {
      console.error('Failed to delete item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border p-3 text-sm">
      <div>
        <h4 className="font-medium">{item.item}</h4>
        {item.expiry_date && (
          <p className="text-xs text-muted-foreground">
            Expires: {new Date(item.expiry_date).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="flex items-center gap-4">
        {(item.quantity !== undefined || item.unit) && (
          <p>
            {item.quantity !== undefined ? item.quantity : ''} {item.unit || ''}
          </p>
        )}
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive/90"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const EmptyState = ({ type }: { type: string }) => (
  <div className="flex flex-col items-center justify-center h-48 text-center p-4">
    <p className="text-muted-foreground">
      You have nothing in your {type}, speak with Kitty and she'll fill these items up for you. Then you can get customised recipes based on what you have in stock!
    </p>
  </div>
);

const InventoryList = ({ items, type }: { items: InventoryItem[], type: string }) => {
  const queryClient = useQueryClient();

  const handleDelete = async (id: string) => {
    await deleteInventoryItem(id);
    queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
  };

  if (items.length === 0) {
    return <EmptyState type={type} />;
  }

  return (
    <ScrollArea className="h-[calc(100vh-12rem)] w-full rounded-md border p-4">
      <div className="space-y-4">
        {items.map((item) => (
          <ListItem 
            key={item.id} 
            item={item} 
            onDelete={handleDelete}
          />
        ))}
      </div>
    </ScrollArea>
  );
};

export const KitchenInventory = ({ 
  open, 
  onOpenChange 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void 
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { data: inventoryItems = [], isLoading: isLoadingInventory } = useQuery({
    queryKey: ['inventory-items'],
    queryFn: fetchInventoryItems,
    enabled: open,
    gcTime: 0,
    staleTime: 0
  });

  const fridgeItems = inventoryItems.filter(item => item.type === 'fridge');
  const cupboardItems = inventoryItems.filter(item => item.type === 'cupboard');
  const freezerItems = inventoryItems.filter(item => item.type === 'freezer');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={`w-full ${isMobile ? 'h-[100dvh] p-0' : 'max-w-md'}`}
      >
        <SheetHeader className="p-6 pb-2">
          <SheetTitle>Kitchen Stock List</SheetTitle>
        </SheetHeader>
        <div className="mt-2 h-full">
          <Tabs defaultValue="fridge" className="h-full">
            <TabsList className="grid w-full grid-cols-3 px-6">
              <TabsTrigger value="fridge" className="flex items-center gap-2">
                <Refrigerator className="h-4 w-4" />
                Fridge
              </TabsTrigger>
              <TabsTrigger value="freezer" className="flex items-center gap-2">
                <Snowflake className="h-4 w-4" />
                Freezer
              </TabsTrigger>
              <TabsTrigger value="cupboard" className="flex items-center gap-2">
                <Package2 className="h-4 w-4" />
                Cupboard
              </TabsTrigger>
            </TabsList>
            <TabsContent value="fridge" className="mt-4 px-6">
              {isLoadingInventory ? (
                <div className="text-center py-4">Loading...</div>
              ) : (
                <InventoryList items={fridgeItems} type="fridge" />
              )}
            </TabsContent>
            <TabsContent value="freezer" className="mt-4 px-6">
              {isLoadingInventory ? (
                <div className="text-center py-4">Loading...</div>
              ) : (
                <InventoryList items={freezerItems} type="freezer" />
              )}
            </TabsContent>
            <TabsContent value="cupboard" className="mt-4 px-6">
              {isLoadingInventory ? (
                <div className="text-center py-4">Loading...</div>
              ) : (
                <InventoryList items={cupboardItems} type="cupboard" />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};